import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, GraduationCap, Award, CalendarDays, ArrowRight, Check } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { KineticHeading, Reveal, CountUp, Marquee, TiltCard } from "@/components/motion";
import { SectionHeading } from "@/components/shared";
import { OssdPlanner } from "@/components/OssdPlanner";
import { ImageBand, DotGrid } from "@/components/visuals";
import { STATS, TRUST, MANIFESTO, STEPS, TESTIMONIALS, FAQS } from "@/data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BAND_IMG = "https://images.unsplash.com/photo-1758270705290-62b6294dd044?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const BAND2_IMG = "https://images.pexels.com/photos/7972324/pexels-photo-7972324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CTA_VIDEO = "https://videos.pexels.com/video-files/7777995/7777995-hd_1280_720_25fps.mp4";
const CTA_POSTER = "https://images.pexels.com/photos/14509172/pexels-photo-14509172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MARQUEE_ITEMS = [
  "Inspected by - Ministry of Education, Ontario",
  "Enrol 365 days a year",
  "Finish a credit in as little as 4 weeks",
  "Ontario Certified Teachers",
  "Transcripts sent directly to OUAC/OCAS",
  "Students in 20+ countries",
  "Up to 12 months of course access",
];

const TRUST_TONES = [
  "bg-brandlight text-brand",
  "bg-aqualight text-cyan-700",
  "bg-grapelight text-violet-700",
  "bg-emerald-100 text-emerald-700",
];

const STEP_TONES = ["bg-brand", "bg-aqua", "bg-grape", "bg-emerald-500", "bg-brand"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ivory">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[540px] w-[540px] rounded-full bg-brand/15 blur-3xl" />
      <div className="pointer-events-none absolute -left-52 top-64 h-[460px] w-[460px] rounded-full bg-aqua/15 blur-3xl" />
      <div className="pointer-events-none absolute right-1/3 top-1/2 h-[420px] w-[420px] rounded-full bg-grape/10 blur-3xl" />
      <DotGrid className="left-[4%] top-24 hidden h-52 w-52 lg:block" />
      <DotGrid className="bottom-16 right-[38%] hidden h-40 w-40 lg:block" />
      <div
        aria-hidden="true"
        data-testid="hero-crest-watermark"
        className="pointer-events-none absolute left-[34%] top-6 hidden h-[480px] w-[480px] -rotate-6 opacity-[0.05] lg:block bg-contain bg-no-repeat"
        style={{ backgroundImage: "url(/logo.webp)" }}
      />

      <div className="container-x grid items-center gap-14 py-20 sm:py-24 lg:grid-cols-12 lg:gap-10 lg:py-28">
        <div className="lg:col-span-7">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-aqua/40 bg-aqualight px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.18em] text-cyan-700">
              <ShieldCheck className="h-3.5 w-3.5" /> Ministry-Inspected · Grants Credits and OSSD
            </span>
          </Reveal>

          <KineticHeading
            testid="hero-heading"
            lines={[
              <>Earn your Ontario</>,
              <>Secondary School Diploma,</>,
              <span className="font-serif italic font-medium text-brand">on your own schedule.</span>,
            ]}
            className="mt-7 font-display text-4xl font-extrabold tracking-tight leading-[1.06] text-ink sm:text-5xl lg:text-[3.9rem]"
          />

          <Reveal delay={0.55}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              <strong className="font-display text-xl font-extrabold text-brand">Eletech Global Ontario School</strong> is a ministry-inspected private online high school where students earn full OSSD and credits — supported by Ontario
              Certified Teachers, with no waitlists and a flexible learning schedule.
            </p>
          </Reveal>

          <Reveal delay={0.85}>
            <div className="mt-12 grid grid-cols-2 gap-6 border-t border-slate-200 pt-8 sm:grid-cols-4" data-testid="hero-stats">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-mono text-2xl font-bold text-ink sm:text-3xl">
                    <CountUp to={s.value} suffix={s.suffix} testid={`hero-stat-${s.value}${s.suffix}`} />
                  </p>
                  <p className="mt-1 text-xs leading-snug text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <motion.div style={{ y: imgY }} className="relative">
            <TiltCard className="relative z-10" data-testid="hero-tilt-card">
              <div className="relative overflow-hidden rounded-3xl border border-ink/10 shadow-2xl shadow-brand/20">
                <img
                  src={HERO_IMG}
                  alt="Students earning their OSSD online together"
                  className="h-[420px] w-full object-cover sm:h-[500px]"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl bg-white/85 px-5 py-4 backdrop-blur-xl" data-testid="hero-glass-badge">
                  <div>
                    <p className="font-display text-sm font-bold text-ink">Enrolment is open</p>
                    <p className="text-xs text-slate-500">Start any course today — access is immediate.</p>
                  </div>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-brand to-amber-500 text-white">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </TiltCard>

            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 top-8 z-20 hidden rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 shadow-xl backdrop-blur-xl sm:block"
              data-testid="hero-float-badge-1"
            >
              <p className="font-mono text-xl font-bold text-cyan-600">100%</p>
              <p className="text-xs font-semibold text-slate-500">higher education admission</p>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -right-3 top-1/2 z-20 hidden rounded-2xl border border-slate-200 bg-white/90 px-5 py-4 shadow-xl backdrop-blur-xl sm:block"
              data-testid="hero-float-badge-2"
            >
              <p className="font-mono text-xl font-bold text-brand">OCT</p>
              <p className="text-xs font-semibold text-slate-500">certified teachers only</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const icons = [ShieldCheck, GraduationCap, Award, CalendarDays];
  return (
    <section className="border-y border-slate-200/70 bg-sand/60" data-testid="trust-bar">
      <div className="container-x grid gap-8 py-14 sm:grid-cols-2 lg:grid-cols-4">
        {TRUST.map((t, i) => {
          const Icon = icons[i];
          return (
            <Reveal key={t.title} delay={i * 0.07} className="flex gap-4">
              <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${TRUST_TONES[i]}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-sm font-bold text-ink">{t.title}</h2>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{t.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function BoostCard() {
  const rows = [
    { code: "SCH4U", name: "Chemistry", delta: 15 },
    { code: "MCV4U", name: "Calculus & Vectors", delta: 12 },
    { code: "ENG4U", name: "English", delta: 8 },
  ];
  const [marks, setMarks] = useState({ SCH4U: 74, MCV4U: 72, ENG4U: 78 });
  const clamp = (v) => Math.max(0, Math.min(100, v || 0));
  const avg = (vals) => (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  const beforeAvg = avg(rows.map((r) => clamp(marks[r.code])));
  const afterAvg = avg(rows.map((r) => Math.min(100, clamp(marks[r.code]) + r.delta)));
  const goTracker = () => {
    const el = document.querySelector('[data-testid="planner-section"]');
    if (el) window.scrollTo({ top: el.offsetTop - 60, behavior: "smooth" });
  };
  return (
    <Reveal className="my-14 sm:my-16">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid="boost-average-card">
        <div className="grid md:grid-cols-12">
          <div className="bg-ink p-8 text-ivory sm:p-10 md:col-span-5">
            <p className="eyebrow !text-brandlight">Boost your average — an example</p>
            <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight">
              What an upgrade <span className="font-serif italic font-medium text-brandlight">actually looks like</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-ivory/70">
              Retake the Grade 12 courses weighing your average down. Your new marks replace the old ones on the
              Ontario Student Transcript — universities see the stronger record.
            </p>
            <p className="mt-6 font-mono text-3xl font-bold text-brandlight" data-testid="boost-average-readout">
              {beforeAvg}% <span className="text-ivory/50">→</span> {afterAvg}%
            </p>
            <p className="text-xs text-ivory/60">your average of these three courses · illustrative uplift</p>
            <button
              onClick={goTracker}
              data-testid="boost-tracker-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-ivory/30 px-5 py-2.5 text-xs font-bold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-ivory/10"
            >
              Track your credits in the Graduation Tracker ↓
            </button>
          </div>
          <div className="flex flex-col justify-center gap-5 p-8 sm:p-10 md:col-span-7">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-brand">Enter your own marks</p>
            {rows.map((r) => (
              <div key={r.code} className="flex items-center gap-4" data-testid={`upgrade-row-${r.code}`}>
                <div className="w-24 flex-shrink-0">
                  <p className="font-mono text-xs font-bold text-ink">{r.code}</p>
                  <p className="text-xs text-slate-500">{r.name}</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="100" value={marks[r.code]}
                      onChange={(e) => setMarks((m) => ({ ...m, [r.code]: Number(e.target.value) }))}
                      data-testid={`upgrade-input-${r.code}`}
                      aria-label={`Your current ${r.name} mark`}
                      className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-right font-mono text-xs text-ink outline-none focus:border-brand"
                    />
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-slate-300 transition-all duration-300" style={{ width: `${clamp(marks[r.code])}%` }} />
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="w-16 text-right font-mono text-xs font-bold text-ink">{Math.min(100, clamp(marks[r.code]) + r.delta)}%</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-gradient-to-r from-brand to-amber-500 transition-all duration-300" style={{ width: `${Math.min(100, clamp(marks[r.code]) + r.delta)}%` }} />
                    </div>
                  </div>
                </div>
                <span className="flex-shrink-0 rounded-full bg-aqualight px-3 py-1 font-mono text-xs font-bold text-cyan-700">+{r.delta}</span>
              </div>
            ))}
            <p className="text-xs text-slate-400">Upgrade gains shown are typical uplifts — your actual marks are up to you (and that's the point).</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function Manifesto() {
  return (
    <section className="container-x py-24 sm:py-32" data-testid="manifesto-section">
      <SectionHeading
        eyebrow="The Eletech Manifesto"
        title={
          <>
            Three reasons families <span className="font-serif italic font-medium text-brand">choose us</span> over a
            bricks-and-mortar school
          </>
        }
      />
      <div className="mt-16 space-y-6">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.num} delay={i * 0.05}>
            <div className="group grid gap-6 rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-2xl hover:shadow-brand/10 sm:p-12 md:grid-cols-12">
              <p className="font-mono text-5xl font-bold text-slate-200 transition-colors duration-300 group-hover:text-brand md:col-span-2">
                {m.num}
              </p>
              <div className="md:col-span-6">
                <h3 className="font-display text-xl font-bold text-ink sm:text-2xl">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{m.text}</p>
              </div>
              <p className="font-serif italic text-lg leading-snug text-brand sm:text-xl md:col-span-4 md:text-right">
                “{m.accent}”
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <BoostCard />
      <Reveal className="mt-12">
        <Link
          to="/why-egos"
          data-testid="manifesto-why-egos-link"
          className="group inline-flex items-center gap-3 font-display text-2xl font-bold text-brand"
        >
          <span className="text-ink">Check Out</span>{" "}
          <span className="sweep">Ten reasons families choose EGOS — plus the comparison every parent asks for</span>
          <span className="transition-transform group-hover:translate-x-1">→</span>
        </Link>
      </Reveal>
    </section>
  );
}

function PlannerSection() {
  return (
    <section className="container-x py-24 sm:py-32" data-testid="planner-section">
      <SectionHeading
        eyebrow="Graduation Tracker"
        title={<>See exactly what stands between you and <span className="font-serif italic font-medium text-brand">the OSSD</span></>}
        desc="Thirty credits, forty community hours, one literacy requirement. Drag the sliders to see what's left."
      />
      <Reveal className="mt-14">
        <OssdPlanner />
      </Reveal>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="relative overflow-hidden bg-ink py-24 text-ivory sm:py-32" data-testid="how-it-works">
      <DotGrid light className="right-[6%] top-16 hidden h-48 w-48 lg:block" />
      <DotGrid light className="bottom-12 left-[4%] hidden h-40 w-40 lg:block" />
      <div className="container-x relative">
        <SectionHeading
          dark
          eyebrow="How It Works"
          title={<>From sign-up to transcript in <span className="font-serif italic font-medium text-brandlight">five steps</span></>}
          desc="No semesters, no start dates, no waiting lists. The whole cycle is built around you."
        />
        <div className="mt-16 grid gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-5">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.08} className="h-full">
              <div className="group h-full rounded-2xl border border-ivory/15 bg-ink2/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/60">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full ${STEP_TONES[i]} font-mono text-sm font-bold text-white`}>
                  {s.num}
                </span>
                <h3 className="mt-5 font-display text-base font-bold">{s.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-ivory/60">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsTeaser() {
  return (
    <section className="container-x py-24 sm:py-32" data-testid="testimonials-teaser">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading
          eyebrow="Student Stories"
          title={<>Graduates who got <span className="font-serif italic font-medium text-brand">exactly where they aimed</span></>}
        />
        <Link to="/tuition#student-stories" data-testid="testimonials-all-btn" className="btn-ghost !px-6 !py-2.5">
            All <span className="text-brand">student stories</span>
        </Link>
      </div>
      <div className="mt-14 grid gap-6 sm:gap-8 md:grid-cols-3">
        {TESTIMONIALS.slice(0, 3).map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08} className="h-full">
            <figure className="card-line flex h-full flex-col p-8">
              <img src="/logo.webp" alt="Eletech Global Ontario School crest" className="h-10 w-10 rounded-full bg-white" />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">{t.quote}</blockquote>
              <figcaption className="mt-6 border-t border-slate-100 pt-4">
                <p className="font-display text-sm font-bold text-ink">{t.name}</p>
                <p className="text-xs text-slate-500">{t.path}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FaqTeaser() {
  const items = FAQS[0].items.concat(FAQS[1].items).slice(0, 4);
  return (
    <section className="container-x py-24 sm:py-32" data-testid="faq-teaser">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Questions, Answered"
            title={<>The things students and parents <span className="font-serif italic font-medium text-brand">ask us most</span></>}
            desc="Still curious? Our admissions team replies within one business day."
          />
          <Link to="/faq" data-testid="faq-teaser-all-btn" className="btn-ghost mt-8 !px-6 !py-2.5">
            Read the full <span className="text-brand">FAQ</span>
          </Link>
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-teaser-accordion">
              {items.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-ink" data-testid={`faq-teaser-item-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="container-x pb-28" data-testid="cta-banner">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-ink px-8 py-24 text-center sm:px-16 sm:py-28">
          <video
            src={CTA_VIDEO}
            poster={CTA_POSTER}
            autoPlay
            muted
            loop
            playsInline
            data-testid="cta-video"
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-ink/60" />
          <DotGrid light className="left-8 top-8 z-10 h-40 w-40" />
          <DotGrid light className="bottom-8 right-8 z-10 h-40 w-40" />
          <div
            aria-hidden="true"
            data-testid="cta-crest-watermark"
            className="pointer-events-none absolute -bottom-20 -right-20 z-10 h-96 w-96 rotate-12 opacity-[0.14] bg-contain bg-no-repeat"
            style={{ backgroundImage: "url(/logo.webp)" }}
          />
          <div className="relative z-10">
            <p className="eyebrow !text-brandlight">Your move</p>
            <h2 className="mx-auto mt-5 max-w-3xl font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-3xl lg:text-4xl">
              Get the standard respected Ontario high school diploma —{" "}
              <span className="font-serif italic font-medium text-brand">
                but now on your own schedule, from any location, with a vast choice of available courses.
              </span>
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/admissions" data-testid="cta-apply-btn" className="btn-accent">Apply in 5 minutes</Link>
              <Link to="/contact" data-testid="cta-contact-btn" className="btn-navy-outline">Talk to our team</Link>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <TrustBar />
      <Manifesto />
      <ImageBand
        src={BAND_IMG}
        alt="A student studying online with EGOS"
        tag="Study your way, from anywhere"
        testid="home-image-band"
      />
      <PlannerSection />
      <Marquee items={MARQUEE_ITEMS} />
      <HowItWorks />
      <TestimonialsTeaser />
      <ImageBand
        src={BAND2_IMG}
        alt="Students walking across a university campus"
        tag="Your pathway to university"
        testid="home-image-band-2"
      />
      <FaqTeaser />
      <CtaBanner />
    </>
  );
}
